import React, { useState, useEffect } from 'react';
import '../css/components/ProductCard.css';
import productImage from '../images/productImage.jpg';
import productImageHover from '../images/productHoverImage.jpg';
import promoBanner from '../images/christmasPromo.jpg';
import shoppingBag from '../images/shopping-bag-movil.png';
import { Link } from 'gatsby';
import { MyContext } from '../context/AppContext';
import useStockValidation from '../utils/useStockValidation';
import checkIcon from '../images/cheque.png';
import warningIcon from '../images/Ico_agotado.png';
import useBrevoEvent from '../utils/useBrevoEvent';
import useFacebookPixel from '../utils/useFacebookPixel';
import { getDisplayPrice, formattedPrice, hasDiscount } from '../utils/priceCalculations';
import { checkLaunchCampaignAccess } from '../utils/launchCampaignCheck';
import { buildCartItem } from '../utils/cartItem';

// addSource: desde dónde se agrega, para Mixpanel. Solo lo pasa el buscador;
// en los demás listados sale de la página (ver addSourceFromPage).
function ProductCard({ imageHover, product, addSource }) {
  const { addToCart } = React.useContext(MyContext);
  const [quantity, setQuantity] = useState('1');
  const [isOutOfStock, setIsOutOfStock] = useState(product?.stock_status !== "instock");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAdded, setIsAdded] = useState(false);
  const [isCartText, setIsCartText] = useState('Agregar');
  const [imageCart, setImageCart] = useState(shoppingBag);
  const [quantityInputReady, setQuantityInputReady] = useState(false); // readOnly hasta primer toque (evita teclado en Safari)
  // Si el producto pertenece a una Launch Campaign activa, mostramos el botón
  // "Ir a promoción" (verde) en vez de "Agregar al carrito" — la compra real
  // se gobierna desde el landing para evitar saltarse el flujo (lead, login,
  // max_qty_per_user, etc.). Lo mismo aplica en buscador y todos los listings
  // porque ProductCard se reusa en todos esos contextos.
  const [launchCampaign, setLaunchCampaign] = useState(null);
  const { trackAddToCart } = useFacebookPixel();
  const { validateStock } = useStockValidation(product?.internal_id);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const productId = product?.internal_id;
    if (!productId) return;
    const base = (process.env.BASE_FRONT || '').replace(/\/$/, '');
    if (!base) return;
    let cancelled = false;
    fetch(`${base}/api/launch-campaigns/by-product/${productId}`, {
      headers: { Accept: 'application/json' },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return;
        setLaunchCampaign(data?.campaign || null);
      })
      .catch(() => { /* silent: si falla, queda como producto normal */ });
    return () => { cancelled = true; };
  }, [product?.internal_id]);

  const useMobileDetection = () => {
    const [isMobile, setIsMobile] = useState(false);
  
    useEffect(() => {
      if (typeof window !== 'undefined') { // Verificación SSR
        const checkMobile = () => {
          const mobile = window.matchMedia("(max-width: 768px)").matches;
          setIsMobile(mobile);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
      }
    }, []);
  
    return isMobile;
  };

  // Evitar que Safari (y otros) enfoquen el input de cantidad al entrar a la página
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const blurQuantityInput = () => {
      const el = document.activeElement;
      if (el?.getAttribute?.('name') === 'quantity' || el?.classList?.contains?.('quantity-input-no-zoom')) {
        el.blur();
      }
    };
    blurQuantityInput();
    const raf = requestAnimationFrame(() => {
      blurQuantityInput();
      setTimeout(blurQuantityInput, 0);
      setTimeout(blurQuantityInput, 100);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // console.log(product);
  const numQuantity = parseInt(quantity, 10) || 0;
  const maxStock = product?.stock_quantity != null ? product.stock_quantity : 9999;

  const decreaseQuantity = () => {
    const n = Math.max(1, numQuantity - 1);
    setQuantity(String(n));
  };

  const increaseQuantity = () => {
    if (product.stock_status === "instock" && maxStock > numQuantity) {
      setQuantity(String(Math.min(numQuantity + 1, maxStock)));
    }
  };

  const handleQuantityChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    setQuantity(raw);
  };

  // Función para obtener el usuario autenticado desde localStorage
  const getLocalStorageItem = (key) => {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key);
    }
    return null;
  };

  const user = JSON.parse(getLocalStorageItem("user"));
  const isAuthenticated = user;
  const { sendCartEvent } = useBrevoEvent(isAuthenticated, user);

  // Las funciones de cálculo de precios ahora están en utils/priceCalculations.js

  const handleClick = async (item) => {
    try {
        setLoading(true);
        
        // Verificar stock_status primero
        if (item.stock_status !== "instock") {
            setIsCartText('Agotado');
            setErrorMessage("Producto agotado.");
            setIsOutOfStock(true);
            return;
        }
        
        const qty = Math.max(1, numQuantity);
        const stockQuantity = await validateStock(qty);

        if (stockQuantity < qty) {
            setIsCartText('Agotado');
            setErrorMessage(`Lo sentimos, solo hay ${stockQuantity} unidades disponibles.`);
            setIsOutOfStock(true);
            return;
        }

        // Validar acceso a campaña de lanzamiento (si aplica)
        const launchCheck = await checkLaunchCampaignAccess(item.internal_id, item.sku, qty);
        if (launchCheck.blocked) {
            if (launchCheck.redirectLogin) {
                const returnUrl = encodeURIComponent(window.location.pathname);
                const msg = encodeURIComponent('Para comprar debes iniciar sesión en tu cuenta');
                window.location.href = `/login?redirect=${returnUrl}&message=${msg}`;
                return;
            }
            if (launchCheck.maxQtyExceeded) {
                setErrorMessage(`Máximo ${launchCheck.max} unidad${launchCheck.max !== 1 ? 'es' : ''} por usuario en esta campaña.`);
                setIsCartText('Límite alcanzado');
                return;
            }
            setErrorMessage(launchCheck.reason || 'No disponible en este momento.');
            setIsCartText('No disponible');
            return;
        }

        const productCart = buildCartItem(item, { quantity: qty, stockQuantity, fallbackImage: productImage });
        
        console.log('Agregando al carrito:', productCart);
        addToCart(productCart, { addSource });

        // Track Facebook Pixel + CAPI event
        console.log('Iniciando envío de evento a Facebook Pixel y CAPI...');
        try {
            trackAddToCart(productCart, user);
            console.log('✅ Evento de Facebook Pixel y CAPI enviado exitosamente');
        } catch (error) {
            console.error('❌ Error al enviar evento a Facebook Pixel y CAPI:', error);
        }

        // Verificar si sendCartEvent existe y es una función antes de usarla
        if (isAuthenticated && user?.email && typeof sendCartEvent === 'function') {
            try {
                console.log('Enviando evento a Brevo');
                await sendCartEvent('cart_updated');
            } catch (brevoError) {
                console.error('Error al enviar evento a Brevo:', brevoError);
            }
        }

        setIsCartText('Agregado');
        setImageCart(checkIcon);
        setIsAdded(true);
        setErrorMessage("");
        console.log('Producto agregado exitosamente');

    } catch (error) {
        console.error("Error detallado al verificar el stock:", error);
        setIsCartText('Agotado');
        setErrorMessage("Hubo un problema al verificar el stock. Por favor, inténtalo de nuevo.");
        setIsOutOfStock(true);
    } finally {
        setLoading(false);
    }
  };


  const getPositionStyles = (positionType, styles) => {
    const { top, bottom, left, right } = styles;

    // Verificar si todos los valores son "0" o están vacíos
    const arePositionsEmpty =
      (!top || top === "0") &&
      (!bottom || bottom === "0") &&
      (!left || left === "0") &&
      (!right || right === "0");

    // Si no hay valores individuales, usar `position_type`
    if (arePositionsEmpty) {
      switch (positionType) {
        case "topLeft":
          return { top: "0", left: "0" };
        case "topRight":
          return { top: "0", right: "0" };
        case "bottomLeft":
          return { bottom: "0", left: "0" };
        case "bottomRight":
          return { bottom: "0", right: "0" };
        case "top":
          return { top: "0", left: "50%", transform: "translateX(-50%)" };
        case "bottom":
          return { 
            position: "absolute",
            bottom: "-3px",
            left: "0",
            right: "0",
            margin: "0 auto",
          };
        case "left":
          return { left: "0", top: "50%", transform: "translateY(-50%)" };
        case "right":
          return { right: "0", top: "50%", transform: "translateY(-50%)" };
        case "center":
          return { 
            position: "absolute",
            top: "50%",
            left: "0",
            right: "0",
            margin: "0 auto",
            transform: "translateY(-50%)",
          };
        default:
          return {}; // Estilos por defecto
      }
    }

      // Usar los valores explícitos si están definidos
      return {
        top: top && top !== "0" ? `${top}px` : "auto",
        bottom: bottom && bottom !== "0" ? `${bottom}px` : "auto",
        left: left && left !== "0" ? `${left}px` : "auto",
        right: right && right !== "0" ? `${right}px` : "auto",
      };
  };

  const isMobile = useMobileDetection();

  if (product) {
    return (
      <div className={`Content__productCard categoriesProductCard ${isOutOfStock ? 'out-of-stock' : ''}`}>
        <a href={`/producto/${product.slug}/`}>
          <div className="Content__productCard--image">

          

            <div className="Content__productCard--image-mainImage activeImage">
            {/* Mostrar insignias */}
            {product.insignias && product.insignias.length > 0 && (
              <div className="Content__productCard--insignias">
                {product.insignias.map((insignia, index) => {
                   // La insignia puede venir null dentro del array; sin media_id la URL
                   // queda como /storage/media/null/... (imagen rota). En ambos casos
                   // se omite. Antes se leía insignia.media_id sin chequear el null y
                   // reventaba el render de la card.
                   if (!insignia || !insignia.media_id) return null;
                   const currentWidth = isMobile && insignia.width_mobile
                   ? `${insignia.width_mobile}px` 
                   : `${insignia.width}px`;
                   const positionStyles = getPositionStyles(insignia.position_type, {
                    top: insignia.top,
                    bottom: insignia.bottom,
                    left: insignia.left,
                    right: insignia.right,
                  });

                  const containerStyles = {
                    position: 'absolute',
                    width: currentWidth === "0px" ? 'auto !important' : currentWidth,
                    height: 'fit-content',
                    ...positionStyles,
                    transform: `
                      rotateX(${insignia.rotation_x || 0}deg)
                      rotateY(${insignia.rotation_y || 0}deg)
                      rotateZ(${insignia.rotation_z || 0}deg)
                    `,
                    opacity: insignia.opacity ? `${insignia.opacity / 100} !important` : '1 !important',
                    marginLeft: insignia.margin_left ? `${insignia.margin_left}px !important` : '0 !important',
                    marginTop: insignia.margin_top ? `${insignia.margin_top}px !important` : '0 !important',
                  };

                  return (
                    <div key={index} className="insignia" style={containerStyles}>
                      <img
                        style={{
                          maxWidth: '100%',
                        }}
                        src={`${process.env.BASE_FRONT}/storage/media/${insignia.media_id}/${insignia.preview}`}
                        alt={insignia.name}
                      />
                    </div>
                  );
                })}
              </div>
              )}
              {imageHover ? (
               <img className="ImageHover" src={`${process.env.BASE_FRONT}/products/${product.recommended_image}`} alt="" />
              ) : (
                <img 
                    src={product.product_card_image 
                      ? `${process.env.BASE_FRONT + product.images[0]?.src}`
                      : product.images?.[0]?.src ? `${process.env.BASE_FRONT}${product.images[0].src}` : productImage} 
                    alt="" 
                  />
              )}
            </div>
            {imageHover && (
              <div className="Content__productCard--image-hoverImage">
                {imageHover ? (
                  <img className="ImageHover" src={product.images?.[0]?.src ? `${process.env.BASE_FRONT}${product.images[0].src}` : productImage} alt="" />
                ) : (
                  <img 
                    src={product.product_card_image 
                      ? `${process.env.BASE_FRONT}${product.images?.[0]?.src || ''}`
                      : product.images?.[0]?.src ? `${process.env.BASE_FRONT}${product.images[0].src}` : productImage} 
                    alt="" 
                  />
                )}
              </div>
            )}
          </div>
        </a>
        <div className="Content__productCard--details">
          <div className="Content__productCard--details-name">
            <a to={`/producto/${product.slug}/`}>
              <h3>{product.name}</h3>
            </a>
          </div>
          <div className="Content__productCard--details-category">
            <p>Botella 750cc</p>
          </div>
          <div className="Content__productCard--details-prices">
              <div className="Content__productCard--details-clubPrice">
                <div className="productCard__details--clubPrice-price">
                  <p>${formattedPrice(getDisplayPrice(product))}</p>
                </div>
                {hasDiscount(product) && (
                  <div className="productCard__details--clubPrice-percent">
                    <p>-{(((product.price - product.sale_price) / product.price) * 100).toFixed(0)}%</p>
                  </div>
                )}
              </div>
              {hasDiscount(product) && (
                <div className="Content__productCard--details-regularPrice">
                  <div className="productCard__details--regularPrice-price">
                    <p>${formattedPrice(getDisplayPrice({...product, sale_price: null}))}</p>
                  </div>
                </div>
              )}
          </div>

          <div className="Content__productCard--actions">
            {/* En productos de Launch Campaign no mostramos los controles de
                cantidad: la cantidad se gobierna desde el landing (siempre 1
                con max_qty_per_user) y el botón solo redirige. */}
            {!launchCampaign && (
              <div className="Content__productCard--addToCart">
                <div className="Content__productCard--addToCart-minus" onClick={decreaseQuantity}>
                  -
                </div>
                <div
                  className="Content__productCard--addToCart-input"
                  onTouchStart={() => !quantityInputReady && setQuantityInputReady(true)}
                  onMouseDown={() => !quantityInputReady && setQuantityInputReady(true)}
                >
                  <input
                    type="text"
                    name="quantity"
                    id={`quantity-${product?.internal_id ?? product?.id ?? 'card'}`}
                    value={quantity}
                    placeholder="0"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    readOnly={!quantityInputReady}
                    disabled={!quantityInputReady}
                    onChange={handleQuantityChange}
                    aria-label="Cantidad"
                    className="quantity-input-no-zoom"
                  />
                </div>
                <div className="Content__productCard--addToCart-plus" onClick={increaseQuantity}>
                  +
                </div>
              </div>
            )}
            <div
              className="Content__productCard--addToCartButton"
              style={launchCampaign ? { gridColumn: '1 / -1' } : undefined}
            >
              {launchCampaign ? (
                <button
                  type="button"
                  onClick={() => { window.location.href = `/lanzamiento/${launchCampaign.slug}/`; }}
                  className="product-card-button launch-campaign-cta"
                  style={{
                    backgroundColor: '#01843C',
                    color: '#fff',
                    borderColor: '#01843C',
                    letterSpacing: '1px',
                    padding: '0 12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span>Ir a promoción</span>
                </button>
              ) : isOutOfStock ? (
                <button disabled className="product-card-button out-of-stock">
                  <img src={warningIcon} alt="Agotado" className="button-icon" />
                  <span>Agotado</span>
                </button>
              ) : (
                <button
                  onClick={() => handleClick(product)}
                  disabled={loading}
                  className={`product-card-button ${isAdded ? 'added' : ''}`}
                >
                  {loading ? (
                    <div className="loading-container">
                      <div className="spinner"></div>
                    </div>
                  ) : (
                    <>
                      <img src={imageCart} alt="Agregar" className="button-icon" />
                      <span>{isCartText}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
            {errorMessage && (
                <div className="Content__productCard--errorMessage">
                    <p>{errorMessage}</p>
                </div>
            )}
        </div>
      </div>
    );
  }
 
}

export default ProductCard;
